import React, { useEffect } from 'react';
import { Accordion, Flex, TextV2 } from '@withjoy/joykit';
import { Photos, Video, PhotoGallery } from '@withjoy/joykit/icons';
import { useTranslation } from '@shared/core';
import { useNavigationPaneContext } from '@website-editor/context/NavigationPaneContext/NavigationPane.context';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useCustomComponentStateContext } from '@website-editor/features/custom-page/context/CustomComponentStateContext/CustomComponentState.context';
import { WebsiteEditorPhotoComponentFragment, WebsiteEditorVideoComponentFragment, WebsiteEditorMediaGalleryComponentFragment } from '@graphql/generated';
import { PHOTO_ACTIONS, PHOTO_ANIMATION_CONFIG, PHOTO_SCREENS } from '../Photo/Photo.constants';
import { VIDEO_ACTIONS, VIDEO_ANIMATION_CONFIG, VIDEO_SCREENS } from '../Video/Video.constants';
import { PHOTO_GALLERY_ACTIONS, PHOTO_GALLERY_ANIMATION_CONFIG, PHOTO_GALLERY_SCREENS } from '../PhotoGallery/PhotoGallery.constants';
import { MediaComponentsAccordionItem } from './MediaComponentsAccordionItem';
import { CustomComponentsProps } from '../CustomComponents';
import { getPhotoNavigations } from '../Photo/Photo.navigations';
import { getVideoNavigations } from '../Video/Video.navigations';
import { getPhotoGalleryNavigations } from '../PhotoGallery/PhotoGallery.navigations';

interface MediaComponentsAccordionProps
  extends Readonly<{
    photoComponent: WebsiteEditorPhotoComponentFragment;
    videoComponent: WebsiteEditorVideoComponentFragment;
    photoGalleryComponent: WebsiteEditorMediaGalleryComponentFragment;
    photoVideoGalleryTitle: string;
    customComponentsProps: CustomComponentsProps;
  }> {}

export const MediaComponentsAccordion: React.FC<MediaComponentsAccordionProps> = ({
  photoComponent,
  videoComponent,
  photoGalleryComponent,
  photoVideoGalleryTitle,
  customComponentsProps,
}) => {
  const { tq } = useTranslation('websiteEditor');
  const { navigateToScreen, screens, addNavigations } = useNavigationPaneContext();

  const { setCustomComponentBlockData: setPhotoData } = useCustomComponentStateContext<WebsiteEditorPhotoComponentFragment>();
  const { setCustomComponentBlockData: setVideoData } = useCustomComponentStateContext<WebsiteEditorVideoComponentFragment>();
  const { setCustomComponentBlockData: setGalleryData } = useCustomComponentStateContext<WebsiteEditorMediaGalleryComponentFragment>();

  // Register navigation screens for Photo, Video, and PhotoGallery components
  // This is necessary because these components aren't rendered in AddComponentList mode,
  // so their navigation hooks never run to register the screens.
  //
  // The defensive checks prevent duplicate registrations. Screens may already be registered if:
  // - User navigated from EditComponentList or SingleComponent mode where individual components
  //   are rendered (and their hooks register screens)
  // - Navigation state persists across mode changes, so screens registered in one mode
  //   remain available when switching to AddComponentList mode
  useEffect(() => {
    if (!screens.includes(PHOTO_SCREENS[0])) {
      const photoNavigations = getPhotoNavigations(customComponentsProps);
      addNavigations(PHOTO_SCREENS, PHOTO_ANIMATION_CONFIG, photoNavigations);
    }
  }, [addNavigations, screens, customComponentsProps]);

  useEffect(() => {
    if (!screens.includes(VIDEO_SCREENS[0])) {
      const videoNavigations = getVideoNavigations(customComponentsProps);
      addNavigations(VIDEO_SCREENS, VIDEO_ANIMATION_CONFIG, videoNavigations);
    }
  }, [addNavigations, screens, customComponentsProps]);

  useEffect(() => {
    if (!screens.includes(PHOTO_GALLERY_SCREENS[0])) {
      const photoGalleryNavigations = getPhotoGalleryNavigations(customComponentsProps);
      addNavigations(PHOTO_GALLERY_SCREENS, PHOTO_GALLERY_ANIMATION_CONFIG, photoGalleryNavigations);
    }
  }, [addNavigations, screens, customComponentsProps]);

  const handlePhotoClick = useEventCallback(() => {
    setPhotoData(photoComponent);
    navigateToScreen(PHOTO_ACTIONS.PHOTO_ADD);
  });

  const handleVideoClick = useEventCallback(() => {
    setVideoData(videoComponent);
    navigateToScreen(VIDEO_ACTIONS.VIDEO_ADD);
  });

  const handlePhotoGalleryClick = useEventCallback(() => {
    setGalleryData(photoGalleryComponent);
    navigateToScreen(PHOTO_GALLERY_ACTIONS.PHOTO_GALLERY_ADD);
  });

  interface AccordionItem
    extends Readonly<{
      key: string;
      icon: React.ReactNode;
      title: string;
      onClick: () => void;
    }> {}

  const accordionItems: AccordionItem[] = [
    {
      key: `photo-${photoComponent.id}`,
      icon: <Photos size={48} />,
      title: tq`Photo`,
      onClick: handlePhotoClick,
    },
    {
      key: `video-${videoComponent.id}`,
      icon: <Video size={48} />,
      title: tq`Video`,
      onClick: handleVideoClick,
    },
    {
      key: `gallery-${photoGalleryComponent.id}`,
      icon: <PhotoGallery size={48} />,
      title: tq`Photo Gallery`,
      onClick: handlePhotoGalleryClick,
    },
  ];

  return (
    <Accordion
      key="media-components-accordion"
      flexDirection="column"
    >
      <Accordion.Item>
        <Accordion.Button
          _hover={{ cursor: 'pointer', backgroundColor: 'mono2', color: 'linkHover' }}
          columnGap={5}
          paddingX={6}
          paddingY={5}
          alignItems="center"
          borderColor="mono3"
          borderWidth="1px"
          borderStyle="solid"
          display="flex"
          borderRadius={12}
          backgroundColor="white"
          width="100%"
        >
          <Photos size={48} />
          <TextV2
            typographyVariant="hed2"
            textAlign="left"
            flex={1}
          >
            {photoVideoGalleryTitle}
          </TextV2>
          <Accordion.Icon />
        </Accordion.Button>
        <Accordion.Panel
          paddingLeft={0}
          paddingRight={0}
          paddingTop={3}
          paddingBottom={0}
        >
          <Flex
            flexDirection="column"
            rowGap={0}
            paddingTop={0}
            borderColor="mono3"
            borderWidth="1px"
            borderStyle="solid"
            borderRadius={12}
            backgroundColor="white"
          >
            {accordionItems.map(item => (
              <MediaComponentsAccordionItem
                key={item.key}
                onClick={item.onClick}
                icon={item.icon}
                title={item.title}
              />
            ))}
          </Flex>
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
};
